import clsx from 'clsx';
import React, { useContext } from 'react';
import { isDefined } from '../../utils';
import { WithInnerRef } from '../../utils/forwardRefs';
import { BottomAction } from './BottomAction';
import { BoxContext } from './Context';
import { DashedSeparator } from './DashedSeparator';
import { Separator } from './Separator';
import { BoxVariant, Level } from './types';
import { getBackgroundForVariant } from './utils';

export { BoxGrid } from './BoxGrid';
export { BoxReset } from './BoxReset';

export interface BoxProps {
  header?: React.ReactNode;
  headerVariant?: BoxVariant;
  isDisabled?: boolean;
  ignorePlacement?: boolean;
  variant?: BoxVariant;
  brandedTopBorder?: boolean;
  contain?: boolean;
  compact?: boolean;
  className?: string;
  ignoreGap?: boolean;
  testId?: string;
}

const BoxComponent: React.FC<React.PropsWithChildren<WithInnerRef<BoxProps, HTMLDivElement>>> = ({
  children,
  header,
  headerVariant,
  variant = 'default',
  isDisabled = false,
  ignorePlacement = false,
  brandedTopBorder = false,
  contain = false,
  compact = false,
  innerRef,
  className,
  ignoreGap,
  testId,
}) => {
  const hasHeader = isDefined(header);

  const { level, variant: parentVariant, isGrid } = useContext(BoxContext);

  const shouldMergeWithParent = !ignorePlacement && parentVariant === variant && !hasHeader;

  if (shouldMergeWithParent) {
    return (
      <Content level={level} ref={innerRef} ignoreGap={ignoreGap} className={className}>
        {children}
      </Content>
    );
  }

  return (
    <Wrapper
      ref={innerRef}
      level={level}
      variant={variant}
      hasHeader={hasHeader}
      isDisabled={isDisabled}
      isGrid={isGrid}
      brandedTopBorder={brandedTopBorder}
      contain={contain}
      data-test-id={testId}
    >
      {hasHeader && (
        <Header
          level={level}
          variant={headerVariant || variant}
          mainVariant={variant}
          compact={compact}
        >
          {header}
        </Header>
      )}
      <OwnContent className={className} level={level} compact={compact} ignoreGap={ignoreGap}>
        <BoxContext.Provider
          value={{ level: level + 1 > 2 ? 2 : ((level + 1) as Level), variant, isGrid: false }}
        >
          {children}
        </BoxContext.Provider>
      </OwnContent>
    </Wrapper>
  );
};

const BoxWithRef = React.forwardRef<HTMLDivElement, React.PropsWithChildren<BoxProps>>(
  (props, ref) => <BoxComponent {...props} innerRef={ref} />
);
export const Box = BoxWithRef as typeof BoxWithRef & {
  Separator: typeof Separator;
  DashedSeparator: typeof DashedSeparator;
  BottomAction: typeof BottomAction;
};

Box.Separator = Separator;
Box.DashedSeparator = DashedSeparator;
Box.BottomAction = BottomAction;

interface WrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: BoxVariant;
  level: Level;
  hasHeader: boolean;
  isDisabled: boolean;
  isGrid: boolean;
  brandedTopBorder: boolean;
  contain: boolean;
  testId?: string;
}

const Wrapper = React.forwardRef<HTMLDivElement, React.PropsWithChildren<WrapperProps>>(
  (
    {
      variant,
      level,
      hasHeader,
      isGrid,
      brandedTopBorder,
      contain,
      isDisabled,
      children,
      testId,
      ...rest
    },
    ref
  ) => {
    return (
      <div
        {...rest}
        ref={ref}
        className={clsx(
          'relative flex w-full flex-col last:mb-0',
          `${getBackgroundForVariant(variant)}`,
          `${getBorderRadiusForLevel(level)}`,
          !isGrid && level === 0 && 'mb-2',
          level === 0 && 'shadow-base',
          brandedTopBorder && 'border-t-8 border-t-brand',
          level > 0 && variant === 'background' && 'border border-gray-100',
          level > 0 && variant === 'default' && 'border border-black/10',
          isDisabled && 'grayscale',
          contain && 'overflow-hidden'
        )}
      >
        {children}
      </div>
    );
  }
);

Wrapper.displayName = 'BoxWrapper';

const Content = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ level: Level; className?: string; ignoreGap?: boolean }>
>(({ level, className, children, ignoreGap }, ref) => (
  <div ref={ref} className={clsx('flex flex-col', !ignoreGap && getGapForLevel(level), className)}>
    {children}
  </div>
));

Content.displayName = 'BoxContent';

const OwnContent = ({
  level,
  children,
  compact,
  className,
  ignoreGap,
}: {
  level: Level;
  compact: boolean;
  children: React.ReactNode;
  ignoreGap?: boolean;
  className?: string;
}) => {
  const paddingForLevel = getPaddingForLevel(level, compact);
  return (
    <Content
      className={clsx('h-full', paddingForLevel, className)}
      level={level}
      ignoreGap={ignoreGap}
    >
      {children}
    </Content>
  );
};

interface HeaderProps {
  level: Level;
  variant: BoxVariant;
  mainVariant: BoxVariant;
  compact: boolean;
}

const Header: React.FC<React.PropsWithChildren<HeaderProps>> = ({
  level,
  variant,
  mainVariant,
  compact,
  children,
}) => {
  return (
    <div
      className={clsx(
        'flex font-medium',
        getBorderRadiusForLevel(level, true),
        getHeaderVariantStyles(variant, mainVariant),
        getPaddingForLevel(level, compact),
        '!py-3'
      )}
    >
      {children}
    </div>
  );
};

const getBorderRadiusForLevel = (level: Level, onlyTop?: boolean): string => {
  switch (level) {
    case 0:
    case 1:
      return clsx(onlyTop ? 'rounded-t-lg' : 'rounded-lg');
    case 2:
    default:
      return clsx(onlyTop ? 'rounded-t-sm' : 'rounded-sm');
  }
};

const getPaddingForLevel = (level: Level, isCompact: boolean): string => {
  switch (level) {
    case 0: {
      return clsx('p-4', !isCompact && 'md:p-7');
    }
    case 1:
    case 2:
    default:
      return clsx('p-4');
  }
};

const getGapForLevel = (level: Level): string => clsx(level === 0 ? 'gap-6' : 'gap-4');

const getHeaderVariantStyles = (variant: BoxVariant, mainVariant: BoxVariant) => {
  const sameVariantAsMain = variant === mainVariant;
  const baseCss = clsx(sameVariantAsMain && 'border-b border-black/10');
  switch (variant) {
    case 'background':
      return clsx(baseCss, `text-black`, sameVariantAsMain ? 'bg-transparent' : 'bg-background');
    case 'filled':
      return 'text-white bg-primary';
    case 'filledLighter':
      return 'text-green-800 bg-green-300 bg-opacity-[0.46]';
    case 'default':
      return clsx(baseCss, 'text-black', sameVariantAsMain ? 'bg-transparent' : 'bg-white');
  }
};
